import React, { Component } from 'react';
import Dropzone from 'react-dropzone';
import classNames from 'classnames';
import { compose } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import shortid from 'shortid';

import { DraggableModal } from 'components/flink-components';
import RadioGroup from '../ActivityOptions/RadioGroup';

import { imagesURL, filesURL, uploadsURL } from 'config';
import { playAudio, stopAudio } from 'actions/audioActions';
import {
  uploadAudioFile,
  generateTTS,
  setTTSVoice,
  setTTSLocale,
  setElevenLabsTTSLocale,
  setElevenLabsTTSAccent,
  setElevenLabsTTSVoice,
  saveEditingActivity,
} from 'actions/flinkMakeActions';
import API from 'api';
import classes from './AddAudio.module.scss';
import Record from './_Record';
import HaitianTtsControls from './HaitianTtsControls';

const isElevenLabsEnabledForLocale = (locale) => {
  const value = locale?.ttsConfig?.elevenLabs;
  return value === true || value?.enable === true || value?.enabled === true;
};

const isPollyEnabledForLocale = (locale) => locale?.ttsConfig?.enable === true;

const getLocaleOptionLabel = (locale) => locale?.displayName || locale?.name || locale?.code;
const DEFAULT_ELEVENLABS_ACCENT = '__any__';
const ELEVENLABS_ADD_AUDIO_SELECTION_STORAGE_KEY = 'flinkMake.addAudio.elevenLabsSelection.v1';
const getVoiceId = (voice) => String(voice?.voiceId || voice?.voice_id || voice?.id || '').trim();
const looksLikeVoiceId = (value) => /^[A-Za-z0-9_-]{16,}$/.test(String(value || '').trim());

const cleanText = (value) => String(value || '').trim();
const normalizeToken = (value) => cleanText(value)
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[_\s]+/g, '-')
  .replace(/-+/g, '-')
  .replace(/^-|-$/g, '');

const titleCaseAccent = (value) => String(value || '')
  .replace(/[-_]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()
  .replace(/\b\w/g, (char) => char.toUpperCase());

const getReadableVoiceLabel = (voice) => {
  const voiceId = getVoiceId(voice);
  const candidates = [
    voice?.name,
    voice?.displayName,
    voice?.display_name,
    voice?.voiceName,
    voice?.voice_name,
    voice?.labels?.name,
    voice?.labels?.display_name,
    voice?.labels?.description,
    voice?.description,
  ];

  return candidates
    .map(cleanText)
    .find((value) => value && value !== voiceId && !looksLikeVoiceId(value)) || '';
};

const getVoiceLabel = (voice) => getReadableVoiceLabel(voice);

const getVoiceSelectLabel = (voice) => getReadableVoiceLabel(voice);

const getVoiceGender = (voice) => {
  const value = cleanText(voice?.gender || voice?.labels?.gender || '').toLowerCase();
  if (value === 'female' || value === 'feminine' || value === 'woman') return 'female';
  if (value === 'male' || value === 'masculine' || value === 'man') return 'male';
  return value;
};

const getVoiceAccent = (voice) => cleanText(
  voice?.accent
  || voice?.labels?.accent
  || voice?.labels?.dialect
  || voice?.labels?.region
  || voice?.label
  || voice?.accentLabel
  || ''
);

const normalizeAccentValue = (value) => {
  let token = normalizeToken(value);
  if (!token || token === 'default' || token === 'any' || token === '__empty__') return DEFAULT_ELEVENLABS_ACCENT;
  token = token.replace(/^(en|es|vi)-/i, '');
  return token || DEFAULT_ELEVENLABS_ACCENT;
};

const readElevenLabsSelectionStore = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return {};
    const raw = window.localStorage.getItem(ELEVENLABS_ADD_AUDIO_SELECTION_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  }
  catch (error) {
    return {};
  }
};

const writeElevenLabsSelectionStore = (store) => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(ELEVENLABS_ADD_AUDIO_SELECTION_STORAGE_KEY, JSON.stringify(store || {}));
  }
  catch (error) {
    // localStorage can be unavailable in private/restricted browser modes.
  }
};

const getStoredElevenLabsSelection = (localeCode) => {
  const key = String(localeCode || '').trim();
  if (!key) return null;
  return readElevenLabsSelectionStore()[key] || null;
};

const setStoredElevenLabsSelection = (localeCode, selection) => {
  const key = String(localeCode || '').trim();
  if (!key) return;

  const store = readElevenLabsSelectionStore();
  const normalizedSelection = selection || {};

  store[key] = {
    accent: '',
    activeGender: '',
    ...normalizedSelection,
    voices: {
      male: '',
      female: '',
      ...((normalizedSelection && normalizedSelection.voices) || {}),
    },
  };
  writeElevenLabsSelectionStore(store);
};

const mergeVoiceMeta = (voice, meta = {}, gender = '') => {
  const voiceId = getVoiceId(voice) || getVoiceId(meta);
  if (!voiceId) return null;

  const labels = {
    ...(voice?.labels || {}),
    ...(meta?.labels || {}),
  };

  const merged = {
    ...voice,
    ...meta,
    voiceId,
    voice_id: voiceId,
    id: voiceId,
    labels,
  };

  const name = getVoiceLabel(merged);
  const accent = getVoiceAccent(merged);

  return {
    ...merged,
    name,
    gender: getVoiceGender(merged) || gender,
    accent,
    labels: {
      ...labels,
      gender: getVoiceGender(merged) || gender,
      accent,
    },
  };
};

const uniqueVoicesById = (items = []) => {
  const seen = new Set();
  return items.filter((voice) => {
    const id = getVoiceId(voice);
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

const sortVoicesByLabel = (voices = []) => [...voices].sort((a, b) => (
  getVoiceSelectLabel(a).localeCompare(getVoiceSelectLabel(b), undefined, { sensitivity: 'base' })
));

const getVoiceIdListKey = (voices = []) => voices
  .map(getVoiceId)
  .filter(Boolean)
  .sort()
  .join('|');

const buildSelectedVoice = (item, gender) => {
  if (!item || typeof item === 'string') return null;
  const voice = mergeVoiceMeta(item, {}, gender);
  if (!voice || !hasReadableVoiceMeta(voice)) return null;
  return {
    ...voice,
    gender,
    labels: {
      ...(voice.labels || {}),
      gender,
    },
  };
};

const getLocaleElevenLabsConfig = (locale) => locale?.ttsConfig?.elevenLabs || {};

const hasReadableVoiceMeta = (voice) => !!getReadableVoiceLabel(voice);

const extractSelectedElevenLabsVoicesFromLocale = (locale) => {
  const config = getLocaleElevenLabsConfig(locale);
  const out = [];

  ['female', 'male'].forEach((gender) => {
    const allowedMeta = Array.isArray(config.allowedVoices?.[gender]) ? config.allowedVoices[gender] : [];

    allowedMeta.forEach((item) => {
      const voice = buildSelectedVoice(item, gender);
      if (voice) out.push(voice);
    });
  });

  return uniqueVoicesById(out);
};

const buildAccentOptionsFromVoices = (voices = []) => {
  const seen = new Set([DEFAULT_ELEVENLABS_ACCENT]);
  const options = [{ value: DEFAULT_ELEVENLABS_ACCENT, label: 'Any' }];

  voices.forEach((voice) => {
    const rawAccent = getVoiceAccent(voice);
    const value = normalizeAccentValue(rawAccent);
    if (!rawAccent || value === DEFAULT_ELEVENLABS_ACCENT || seen.has(value)) return;
    seen.add(value);
    options.push({ value, label: titleCaseAccent(rawAccent) });
  });

  return options;
};

class AddAudio extends Component {
  state = {
    currentUrl: '',
    show: false,
    file: null,
    elevenLabsVoices: [],
    elevenLabsLoadedLocale: '',
    elevenLabsDefaults: {
      male: '',
      female: '',
    },
    elevenLabsAccentOptions: [],
    elevenLabsVoicesLoading: false,
    elevenLabsVoicesError: '',
    elevenLabsActiveGender: '',
    elevenLabsLocaleOverrides: {},
  };

  componentDidUpdate(prevProps, prevState) {
    const { currentUrl } = this.state;
    const { current: prevCurrent, playingSrc, isPlaying, autoOpenKey: prevAutoOpenKey } = prevProps;
    const {
      current,
      tts,
      locales,
      setTTSLocale,
      elevenLabsLocales,
      setElevenLabsTTSLocale,
      autoOpenKey,
    } = this.props;

    if (autoOpenKey !== undefined && autoOpenKey !== prevAutoOpenKey && !this.state.show && !this.props.disabledButton) {
      this.setState({ show: true, file: null });
      return;
    }

    if (current !== prevCurrent && currentUrl === playingSrc && isPlaying) {
      this.stopHandler();
    }

    const vtLocale = this.getVersionTypeLocale();

    const vtLocalePollyEnabled = vtLocale && locales.some((locale) => locale.code === vtLocale);

    if (vtLocalePollyEnabled && tts.locale !== vtLocale) {
      setTTSLocale(vtLocale);
    }
    else if (!tts.locale && locales.length) {
      setTTSLocale(locales[0].code);
    }

    const modalJustOpened = this.state.show && !prevState.show;
    const hasElevenLabsLocale = elevenLabsLocales.length > 0;

    if (modalJustOpened && hasElevenLabsLocale && this.isElevenLabsAvailableForCurrentLocale()) {
      const elevenState = tts.elevenLabs || {};
      const selectedLocale = vtLocale || elevenState.locale || tts.locale;
      const supportedSelectedLocale = elevenLabsLocales.some((locale) => locale.code === selectedLocale);
      const nextLocale = supportedSelectedLocale ? selectedLocale : (vtLocale || tts.locale || elevenLabsLocales[0].code);

      if (nextLocale && elevenState.locale !== nextLocale) {
        setElevenLabsTTSLocale(nextLocale);
      }

      this.loadElevenLabsVoices(nextLocale);
      return;
    }

    const currentElevenLocale = this.getEffectiveElevenLabsLocale();

    // Never compare against prevProps here. With versionType locales prevProps can
    // keep the app TTS locale while the effective ElevenLabs locale is fixed by
    // the activity version. That causes componentDidUpdate -> setState ->
    // componentDidUpdate loops and exhausts browser/API resources.
    if (
      this.state.show
      && hasElevenLabsLocale
      && this.isElevenLabsAvailableForCurrentLocale()
      && currentElevenLocale
      && currentElevenLocale !== this.state.elevenLabsLoadedLocale
    ) {
      this.loadElevenLabsVoices(currentElevenLocale);
    }
  }

  componentWillUnmount() {
    stopAudio();
  }

  onDrop = (files) => {
    this.setState({ file: files[0] }, this.uploadHandler);
  };

  getVersionTypeLocale = () => {
    const { versionType, isWordAgs, allowedLocales } = this.props;

    // The Word AG editor explicitly binds individual audio to one selected
    // locale. Other AddAudio consumers retain their existing language choice.
    if (isWordAgs && Array.isArray(allowedLocales) && allowedLocales.length === 1) {
      return allowedLocales[0];
    }

    return (!isWordAgs && versionType && String(versionType).includes('_'))
      ? String(versionType).split('_').slice(-1)[0]
      : null;
  };

  getEffectiveHaitianLocale = () => {
    const { allLocales = [], tts } = this.props;
    const versionLocale = this.getVersionTypeLocale();

    if (versionLocale) {
      return allLocales.find((locale) => locale.code === versionLocale) || null;
    }

    if (allLocales.length === 1) return allLocales[0];

    const selectedLocale = tts?.elevenLabs?.locale || tts?.locale || '';
    return allLocales.find((locale) => locale.code === selectedLocale) || null;
  };

  getEffectivePollyLocale = () => {
    const { tts, locales } = this.props;
    const vtLocale = this.getVersionTypeLocale();

    if (vtLocale && locales.some((locale) => locale.code === vtLocale)) {
      return vtLocale;
    }

    if (tts.locale && locales.some((locale) => locale.code === tts.locale)) {
      return tts.locale;
    }

    return locales[0]?.code;
  };

  isElevenLabsAvailableForCurrentLocale = () => {
    const versionLocale = this.getVersionTypeLocale();

    if (!versionLocale) {
      return this.props.elevenLabsLocales.length > 0;
    }

    const currentLocale = this.props.allLocales.find((locale) => locale.code === versionLocale);
    return isElevenLabsEnabledForLocale(currentLocale);
  };

  getEffectiveElevenLabsLocale = () => {
    const { tts, elevenLabsLocales } = this.props;
    const elevenState = tts.elevenLabs || {};
    const versionLocale = this.getVersionTypeLocale();

    if (versionLocale && elevenLabsLocales.some((locale) => locale.code === versionLocale)) {
      return versionLocale;
    }

    const selected = elevenState.locale || tts.locale;

    if (elevenLabsLocales.some((locale) => locale.code === selected)) {
      return selected;
    }

    return elevenLabsLocales[0]?.code || '';
  };

  getElevenLabsLocaleModel = (localeCode = this.getEffectiveElevenLabsLocale()) => (
    this.state.elevenLabsLocaleOverrides[localeCode]
    || this.props.allLocales.find((locale) => locale.code === localeCode)
  );

  fetchLatestElevenLabsLocaleModel = (localeCode) => {
    const baseLocale = this.state.elevenLabsLocaleOverrides[localeCode]
      || this.props.allLocales.find((locale) => locale.code === localeCode);

    return baseLocale || null;
  };

  getElevenLabsAccentOptions = () => {
    if (this.state.elevenLabsLoadedLocale && this.state.elevenLabsLoadedLocale !== this.getEffectiveElevenLabsLocale()) {
      return [{ value: DEFAULT_ELEVENLABS_ACCENT, label: 'Any' }];
    }

    return buildAccentOptionsFromVoices(this.state.elevenLabsVoices || []);
  };

  getEffectiveElevenLabsAccent = () => {
    const { tts } = this.props;
    const selectedAccent = normalizeAccentValue(tts.elevenLabs?.accent || '');
    const accents = this.getElevenLabsAccentOptions();

    if (selectedAccent && accents.some((accent) => accent.value === selectedAccent)) {
      return selectedAccent;
    }

    return DEFAULT_ELEVENLABS_ACCENT;
  };

  changeElevenLabsAccent = (accent) => {
    const { setElevenLabsTTSAccent, setElevenLabsTTSVoice } = this.props;
    const normalized = normalizeAccentValue(accent);
    const nextAccent = normalized === DEFAULT_ELEVENLABS_ACCENT ? '' : normalized;

    setElevenLabsTTSAccent(nextAccent);
    setElevenLabsTTSVoice('female', '');
    setElevenLabsTTSVoice('male', '');
    this.setState({ elevenLabsActiveGender: '' });
    this.persistElevenLabsSelection({
      accent: nextAccent,
      voices: { female: '', male: '' },
      activeGender: '',
    });
  };

  getVoicesByGenderForAccent = (gender, accentValue = this.getEffectiveElevenLabsAccent()) => {
    if (this.state.elevenLabsLoadedLocale && this.state.elevenLabsLoadedLocale !== this.getEffectiveElevenLabsLocale()) {
      return [];
    }

    const voices = this.state.elevenLabsVoices || [];
    const accent = normalizeAccentValue(accentValue);

    return sortVoicesByLabel(voices.filter((voice) => {
      const matchesGender = getVoiceGender(voice) === gender;
      const voiceAccent = normalizeAccentValue(getVoiceAccent(voice));
      const matchesAccent = accent === DEFAULT_ELEVENLABS_ACCENT || voiceAccent === accent;
      return matchesGender && matchesAccent;
    }));
  };

  getVoicesByGender = (gender) => this.getVoicesByGenderForAccent(gender);

  getResolvedElevenLabsVoiceId = (gender) => {
    const { tts } = this.props;
    const selectedVoiceId = tts.elevenLabs?.voices?.[gender] || '';
    const voices = this.getVoicesByGender(gender);

    if (selectedVoiceId && voices.some((voice) => getVoiceId(voice) === selectedVoiceId)) {
      return selectedVoiceId;
    }

    return '';
  };

  getActiveElevenLabsGender = () => {
    const activeGender = this.state.elevenLabsActiveGender;
    const femaleVoiceId = this.getResolvedElevenLabsVoiceId('female');
    const maleVoiceId = this.getResolvedElevenLabsVoiceId('male');

    if (activeGender === 'female' && femaleVoiceId) return 'female';
    if (activeGender === 'male' && maleVoiceId) return 'male';

    if (femaleVoiceId && !maleVoiceId) return 'female';
    if (maleVoiceId && !femaleVoiceId) return 'male';

    return '';
  };

  getVisibleElevenLabsVoiceId = (gender) => (
    this.getActiveElevenLabsGender() === gender
      ? this.getResolvedElevenLabsVoiceId(gender)
      : ''
  );

  persistElevenLabsSelection = ({
    localeCode = this.getEffectiveElevenLabsLocale(),
    accent = this.props.tts.elevenLabs?.accent || '',
    voices = this.props.tts.elevenLabs?.voices || {},
    activeGender = this.state.elevenLabsActiveGender,
  } = {}) => {
    const normalizedAccent = normalizeAccentValue(accent);

    setStoredElevenLabsSelection(localeCode, {
      accent: normalizedAccent === DEFAULT_ELEVENLABS_ACCENT ? '' : normalizedAccent,
      voices: {
        female: voices.female || '',
        male: voices.male || '',
      },
      activeGender,
    });
  };

  restoreElevenLabsSelection = (localeCode = this.getEffectiveElevenLabsLocale()) => {
    const saved = getStoredElevenLabsSelection(localeCode);
    if (!saved) return false;

    const {
      tts,
      setElevenLabsTTSAccent,
      setElevenLabsTTSVoice,
    } = this.props;

    const accentOptions = this.getElevenLabsAccentOptions();
    const savedAccent = normalizeAccentValue(saved.accent || '');
    const hasSavedAccent = savedAccent !== DEFAULT_ELEVENLABS_ACCENT
      && accentOptions.some((accent) => accent.value === savedAccent);
    const restoredAccent = hasSavedAccent ? savedAccent : DEFAULT_ELEVENLABS_ACCENT;
    const restoredAccentValue = restoredAccent === DEFAULT_ELEVENLABS_ACCENT ? '' : restoredAccent;
    const savedVoices = saved.voices || {};

    const resolveVoiceId = (gender) => {
      const voiceId = String(savedVoices[gender] || '').trim();
      if (!voiceId) return '';

      return this.getVoicesByGenderForAccent(gender, restoredAccent)
        .some((voice) => getVoiceId(voice) === voiceId) ? voiceId : '';
    };

    const femaleVoiceId = resolveVoiceId('female');
    const maleVoiceId = resolveVoiceId('male');
    let activeGender = saved.activeGender === 'male' || saved.activeGender === 'female'
      ? saved.activeGender
      : '';

    if (activeGender === 'female' && !femaleVoiceId) activeGender = '';
    if (activeGender === 'male' && !maleVoiceId) activeGender = '';
    if (!activeGender) activeGender = femaleVoiceId ? 'female' : maleVoiceId ? 'male' : '';

    const nextFemaleVoiceId = activeGender === 'female' ? femaleVoiceId : '';
    const nextMaleVoiceId = activeGender === 'male' ? maleVoiceId : '';
    const elevenState = tts.elevenLabs || {};

    if (normalizeAccentValue(elevenState.accent || '') !== restoredAccent) {
      setElevenLabsTTSAccent(restoredAccentValue);
    }

    if ((elevenState.voices?.female || '') !== nextFemaleVoiceId) {
      setElevenLabsTTSVoice('female', nextFemaleVoiceId);
    }

    if ((elevenState.voices?.male || '') !== nextMaleVoiceId) {
      setElevenLabsTTSVoice('male', nextMaleVoiceId);
    }

    if (this.state.elevenLabsActiveGender !== activeGender) {
      this.setState({ elevenLabsActiveGender: activeGender });
    }

    return !!activeGender || !!restoredAccentValue;
  };

  loadElevenLabsVoices = (localeCode = this.getEffectiveElevenLabsLocale()) => {
    const normalizedLocaleCode = String(localeCode || '').trim();
    if (!normalizedLocaleCode) return;

    const localeModel = this.fetchLatestElevenLabsLocaleModel(normalizedLocaleCode);

    const selectedVoices = uniqueVoicesById(localeModel ? extractSelectedElevenLabsVoicesFromLocale(localeModel) : [])
      .map((voice) => ({ ...voice, localeCode: normalizedLocaleCode }))
      .filter(hasReadableVoiceMeta);

    const nextVoices = sortVoicesByLabel(selectedVoices);
    const nextVoiceKey = getVoiceIdListKey(nextVoices);
    const currentVoiceKey = getVoiceIdListKey(this.state.elevenLabsVoices || []);

    if (
      this.state.elevenLabsLoadedLocale === normalizedLocaleCode
      && currentVoiceKey === nextVoiceKey
      && !this.state.elevenLabsVoicesLoading
    ) {
      this.restoreElevenLabsSelection(normalizedLocaleCode);
      return;
    }

    this.setState({
      elevenLabsVoices: nextVoices,
      elevenLabsLoadedLocale: normalizedLocaleCode,
      elevenLabsDefaults: { male: '', female: '' },
      elevenLabsAccentOptions: buildAccentOptionsFromVoices(nextVoices),
      elevenLabsVoicesLoading: false,
      elevenLabsVoicesError: '',
    }, () => {
      this.ensureElevenLabsVoiceSelection();
      this.restoreElevenLabsSelection(normalizedLocaleCode);
    });
  };

  ensureElevenLabsVoiceSelection = () => {
    const {
      tts,
      setElevenLabsTTSAccent,
      setElevenLabsTTSVoice,
    } = this.props;

    const accents = this.getElevenLabsAccentOptions();
    const currentAccent = normalizeAccentValue(tts.elevenLabs?.accent || '');

    if (currentAccent !== DEFAULT_ELEVENLABS_ACCENT && !accents.some((accent) => accent.value === currentAccent)) {
      setElevenLabsTTSAccent('');
    }

    const femaleVoiceId = tts.elevenLabs?.voices?.female || '';
    const maleVoiceId = tts.elevenLabs?.voices?.male || '';
    const femaleVoices = this.getVoicesByGender('female');
    const maleVoices = this.getVoicesByGender('male');
    const femaleStillAvailable = femaleVoiceId && femaleVoices.some((voice) => getVoiceId(voice) === femaleVoiceId);
    const maleStillAvailable = maleVoiceId && maleVoices.some((voice) => getVoiceId(voice) === maleVoiceId);

    if (femaleVoiceId && !femaleStillAvailable) {
      setElevenLabsTTSVoice('female', '');
    }

    if (maleVoiceId && !maleStillAvailable) {
      setElevenLabsTTSVoice('male', '');
    }

    if (femaleStillAvailable && maleStillAvailable) {
      if (this.state.elevenLabsActiveGender === 'male') {
        setElevenLabsTTSVoice('female', '');
        return;
      }

      if (this.state.elevenLabsActiveGender === 'female') {
        setElevenLabsTTSVoice('male', '');
        return;
      }

      setElevenLabsTTSVoice('female', '');
      setElevenLabsTTSVoice('male', '');
    }
  };

  onGeneratePollyTTS = async () => {
    const {
      isUrl,
      path,
      removeOldOnChange,
      current,
      tts,
      locales,
      useFileBucket,
      generateTTS,
      associatedText: text,
      onGeneratePollyOverride,
    } = this.props;

    if (typeof onGeneratePollyOverride === 'function') {
      if (!locales.length) return;
      const locale = this.getEffectivePollyLocale();
      this.setState({ show: false, file: null }, () => {
        onGeneratePollyOverride({ locale, voice: tts.voice, provider: 'polly' });
      });
      return;
    }

    if (!locales.length)
      return console.warn('Tried to generate TTS with no available locales');

    this.setState({ show: false, file: null });

    const filename = await generateTTS({
      path,
      voice: tts.voice,
      text,
      useFileBucket,
      locale: this.getEffectivePollyLocale(),
      provider: 'polly',
      fileToDelete: !isUrl && removeOldOnChange ? current : null,
    });

    if (!filename)
      return console.warn('No file returned from generate TTS function');

    this.changeHandler(filename);
  }

  getSelectedElevenLabsVoice = (gender) => {
    const voiceId = this.getVisibleElevenLabsVoiceId(gender);
    if (!voiceId) return null;

    return (this.getVoicesByGender(gender) || []).find((voice) => getVoiceId(voice) === voiceId) || null;
  };

  onGenerateHaitianTTS = async ({
    locale,
    voice = 'female',
    haitianVoiceId = '',
    haitianAccent = '',
    haitianProvider = '',
  } = {}) => {
    const {
      path, associatedText: text, useFileBucket, current, isUrl, removeOldOnChange, generateTTS,
      onGenerateHaitianOverride,
    } = this.props;

    if (!locale) return console.warn('Tried to generate Haitian Creole TTS with no locale');
    if (!text || typeof text !== 'string') return console.warn('Tried to generate Haitian Creole TTS with no associated text');

    if (typeof onGenerateHaitianOverride === 'function') {
      this.setState({ show: false, file: null }, () => {
        onGenerateHaitianOverride({
          locale,
          provider: 'haitian-creole',
          voice,
          haitianVoiceId,
          haitianAccent,
          haitianProvider,
        });
      });
      return;
    }

    this.setState({ show: false, file: null });

    const filename = await generateTTS({
      path,
      voice,
      text,
      useFileBucket,
      locale,
      provider: 'haitian-creole',
      haitianVoiceId,
      haitianAccent,
      fileToDelete: !isUrl && removeOldOnChange ? current : null,
    });

    if (!filename) return console.warn('No file returned from Haitian Creole TTS');
    this.changeHandler(filename);
  };

  onGenerateElevenLabsTTS = async () => {
    const {
      isUrl,
      path,
      removeOldOnChange,
      current,
      useFileBucket,
      generateTTS,
      associatedText: text,
      elevenLabsLocales,
      onGenerateElevenLabsOverride,
    } = this.props;

    if (!elevenLabsLocales.length)
      return console.warn('Tried to generate ElevenLabs TTS with no enabled locales');

    const locale = this.getEffectiveElevenLabsLocale();
    const elevenLabsGender = this.getActiveElevenLabsGender();
    const elevenLabsVoiceId = elevenLabsGender ? this.getVisibleElevenLabsVoiceId(elevenLabsGender) : '';

    if (!elevenLabsVoiceId)
      return console.warn('Tried to generate ElevenLabs TTS with no selected voice');

    if (typeof onGenerateElevenLabsOverride === 'function') {
      const selectedVoice = this.getSelectedElevenLabsVoice(elevenLabsGender);
      const accentValue = this.getEffectiveElevenLabsAccent();
      const normalizedAccent = accentValue === DEFAULT_ELEVENLABS_ACCENT ? '' : accentValue;

      this.setState({ show: false, file: null }, () => {
        onGenerateElevenLabsOverride({
          locale,
          voice: elevenLabsGender,
          elevenLabsVoiceId,
          voiceLabel: getVoiceSelectLabel(selectedVoice) || elevenLabsVoiceId,
          accent: normalizedAccent,
        });
      });
      return;
    }

    this.setState({ show: false, file: null });

    const filename = await generateTTS({
      path,
      voice: elevenLabsGender,
      text,
      useFileBucket,
      locale,
      provider: 'elevenlabs',
      elevenLabsVoiceId,
      fileToDelete: !isUrl && removeOldOnChange ? current : null,
    });

    if (!filename)
      return console.warn('No file returned from ElevenLabs TTS function');

    this.changeHandler(filename);
  }

  playHandler = () => {
    const { current, path, isUrl, useFileBucket } = this.props;

    const base = (useFileBucket ? filesURL : uploadsURL).replace(/\/+$/, '');

    let cleanPath = (path || '').replace(/^https?:\/\/[^/]+\/flink-2\.0-fs\//, '');
    cleanPath = cleanPath.replace(/^\/+/, '');

    if (cleanPath && !cleanPath.endsWith('/')) cleanPath += '/';

    const file = String(current || '').replace(/^\/+/, '');

    let audioUrl = isUrl ? current : `${base}/${cleanPath}${file}`;

    audioUrl = audioUrl.replace(/([^:]\/)\/+/g, '$1');

    playAudio(audioUrl);
    this.setState({ currentUrl: audioUrl });
  };

  stopHandler = () => {
    stopAudio();
  };

  removeHandler = () => {
    this.stopHandler();
    const { path, current, isUrl, preserveFiles } = this.props;

    if (!isUrl && !preserveFiles) {
      const key = `${path}/${current}`;
      API.storage.deleteFiles([key], false);
    }

    this.changeHandler();
  };

  uploadHandler = async () => {
    const {
      path,
      isUrl,
      withId,
      current,
      uploadAudioFile,
      filename: filenameFromProps,
      removeOldOnChange,
      useFileBucket,
    } = this.props;

    const { file } = this.state;

    const [, originFileName, extension] = file.name.match(/\/?([^/]+)\.(\S+)$/);

    const withoutExt = filenameFromProps ? filenameFromProps : originFileName;
    const id = withId ? '_' + shortid.generate() : '';

    const filename = `${withoutExt}${id}.${extension}`;

    const params = {
      path,
      file: this.state.file,
      filename,
      fileToDelete: !isUrl && removeOldOnChange ? current : null,
      useUploadBucket: !useFileBucket,
    };

    const success = await uploadAudioFile(params);

    if (!success) return;

    this.changeHandler(filename);
    this.setState({ show: false, file: null });
  };

  changeHandler = (value) => {
    const {
      onChangeHandler,
      saveAfterChange,
      saveEditingActivity,
    } = this.props;

    onChangeHandler(value);
    saveAfterChange && saveEditingActivity();
  };

  useRecorderFile = async (file) => {
    const {
      path,
      isUrl,
      current,
      filename: filenameFromProps,
      uploadAudioFile,
      removeOldOnChange,
      useFileBucket,
    } = this.props;

    const filename = filenameFromProps || file.name;

    const params = {
      path,
      file,
      filename,
      fileToDelete: !isUrl && removeOldOnChange ? current : null,
      useUploadBucket: !useFileBucket,
    };

    const success = await uploadAudioFile(params);

    if (!success) return;

    this.changeHandler(filename);
    this.setState({ show: false, file: null });
  };

  toggleShow = () => {
    const { onModalClose } = this.props;
    stopAudio();

    this.setState((prevState) => ({ show: !prevState.show }), () => {
      if (!this.state.show && typeof onModalClose === 'function') {
        onModalClose();
      }
    });
  };

  renderPollyControls = () => {
    const {
      translate,
      tts,
      locales,
      setTTSVoice,
      setTTSLocale,
      versionType,
      isWordAgs,
      associatedText = '',
      disabledButton,
    } = this.props;

    if (!locales.length) return null;

    const vtLocale = this.getVersionTypeLocale();
    const selectLocales = (!isWordAgs && !!versionType && vtLocale)
      ? locales.filter((locale) => locale.code === vtLocale)
      : locales;

    if (!selectLocales.length) return null;

    const effectiveLocale = this.getEffectivePollyLocale();
    const selectValue = selectLocales.some((locale) => locale.code === effectiveLocale)
      ? effectiveLocale
      : selectLocales[0]?.code || '';

    return (
      <div className={classes.row}>
        <button
          type='button'
          title="Generate TTS"
          className={`${classes['tts-btn']} btn-effect`}
          onClick={this.onGeneratePollyTTS}
          disabled={!associatedText || disabledButton}
        >
          <img
            src={`${imagesURL}/FlinkMake/TTS.png`}
            alt="TTS"
            width={50}
            height={50}
          />
        </button>
        <div className={classes['tts-picker']}>
          <select
            value={selectValue}
            onChange={(event) => setTTSLocale(event.target.value)}
          >
            {selectLocales.map((locale) => (
              <option
                label={getLocaleOptionLabel(locale)}
                value={locale.code}
                key={`tts-locale-${locale._id || locale.code}`}
              />
            ))}
          </select>

          <div style={{ display: 'flex' }}>
            <RadioGroup
              name="voice"
              changeHandler={(_, value) => setTTSVoice(value)}
              current={tts}
              options={[
                { label: translate(0, 'Male'), value: 'male' },
                { label: translate(0, 'Female'), value: 'female' },
              ]}
            />
          </div>
        </div>
      </div>
    );
  };

  changeElevenLabsVoice = (gender, voiceId) => {
    const { setElevenLabsTTSVoice, tts } = this.props;
    const otherGender = gender === 'male' ? 'female' : 'male';
    const currentVoices = tts.elevenLabs?.voices || {};
    const nextVoices = {
      female: currentVoices.female || '',
      male: currentVoices.male || '',
      [gender]: voiceId,
    };
    let nextActiveGender = this.state.elevenLabsActiveGender;

    setElevenLabsTTSVoice(gender, voiceId);

    if (voiceId) {
      nextVoices[otherGender] = '';
      nextActiveGender = gender;
      setElevenLabsTTSVoice(otherGender, '');
      this.setState({ elevenLabsActiveGender: gender });
    }
    else if (this.state.elevenLabsActiveGender === gender) {
      nextActiveGender = '';
      this.setState({ elevenLabsActiveGender: '' });
    }

    this.persistElevenLabsSelection({
      voices: nextVoices,
      activeGender: nextActiveGender,
    });
  };

  renderElevenLabsControls = () => {
    const {
      elevenLabsLocales,
      setElevenLabsTTSLocale,
      associatedText = '',
      disabledButton,
    } = this.props;

    const {
      elevenLabsVoices,
      elevenLabsVoicesLoading,
      elevenLabsVoicesError,
    } = this.state;

    if (!elevenLabsLocales.length || !this.isElevenLabsAvailableForCurrentLocale()) return null;

    const localeValue = this.getEffectiveElevenLabsLocale();
    const accentOptions = this.getElevenLabsAccentOptions();
    const accentValue = this.getEffectiveElevenLabsAccent();
    const activeGender = this.getActiveElevenLabsGender();
    const femaleVoices = this.getVoicesByGender('female');
    const maleVoices = this.getVoicesByGender('male');
    const femaleVoiceId = this.getVisibleElevenLabsVoiceId('female');
    const maleVoiceId = this.getVisibleElevenLabsVoiceId('male');
    const activeVoiceId = activeGender === 'male' ? maleVoiceId : activeGender === 'female' ? femaleVoiceId : '';
    const canGenerate = !!associatedText && !!localeValue && !!activeVoiceId && !disabledButton && !elevenLabsVoicesLoading;

    const renderVoiceSelect = (gender, voices, selectedVoiceId) => (
      <label className={classes['elevenlabs-field']}>
        <span>{gender === 'male' ? 'Male' : 'Female'}</span>
        <select
          className={classes['tts-select']}
          value={selectedVoiceId}
          onChange={(event) => this.changeElevenLabsVoice(gender, event.target.value)}
          disabled={!voices.length || elevenLabsVoicesLoading}
        >
          <option value="">{voices.length ? `Select ${gender} voice` : (elevenLabsVoicesLoading ? 'Loading...' : `No ${gender} voices`)}</option>
          {voices.map((voice) => (
            <option value={getVoiceId(voice)} key={`elevenlabs-${gender}-${getVoiceId(voice)}`}>
              {getVoiceSelectLabel(voice)}
            </option>
          ))}
        </select>
      </label>
    );

    return (
      <div className={classes['elevenlabs-tts-block']}>
        <button
          type='button'
          title="Generate ElevenLabs TTS"
          className={`${classes['tts-btn']} ${classes['elevenlabs-btn']} btn-effect`}
          onClick={this.onGenerateElevenLabsTTS}
          disabled={!canGenerate}
        >
          <img
            src={`${imagesURL}/FlinkMake/11.png`}
            alt="ElevenLabs TTS"
            width={50}
            height={50}
          />
        </button>

        <div className={classes['elevenlabs-controls']}>
          <label className={classes['elevenlabs-field']}>
            <span>Language</span>
            <select
              className={classes['tts-select']}
              value={localeValue}
              onChange={(event) => {
                setElevenLabsTTSLocale(event.target.value);
                this.props.setElevenLabsTTSAccent('');
                this.props.setElevenLabsTTSVoice('female', '');
                this.props.setElevenLabsTTSVoice('male', '');
                this.setState({ elevenLabsActiveGender: '' }, () => this.loadElevenLabsVoices(event.target.value));
              }}
            >
              {elevenLabsLocales.map((locale) => (
                <option
                  label={getLocaleOptionLabel(locale)}
                  value={locale.code}
                  key={`elevenlabs-locale-${locale._id || locale.code}`}
                />
              ))}
            </select>
          </label>

          <label className={classes['elevenlabs-field']}>
            <span>Accent</span>
            <select
              className={classes['tts-select']}
              value={accentValue}
              onChange={(event) => this.changeElevenLabsAccent(event.target.value)}
              disabled={!accentOptions.length || elevenLabsVoicesLoading}
            >
              {accentOptions.length ? accentOptions.map((accent) => (
                <option value={accent.value} key={`elevenlabs-accent-${accent.value}`}>{accent.label}</option>
              )) : (
                <option value={DEFAULT_ELEVENLABS_ACCENT}>{elevenLabsVoicesLoading ? 'Loading...' : 'Any'}</option>
              )}
            </select>
          </label>

          {renderVoiceSelect('female', femaleVoices, femaleVoiceId)}
          {renderVoiceSelect('male', maleVoices, maleVoiceId)}

          {elevenLabsVoicesError && (
            <div className={classes['elevenlabs-error']}>{elevenLabsVoicesError}</div>
          )}

          {!elevenLabsVoicesLoading && !elevenLabsVoicesError && !elevenLabsVoices.length && (
            <div className={classes['elevenlabs-error']}>No selected Locale Admin voices for this language/accent.</div>
          )}
        </div>
      </div>
    );
  };

  render() {
    const { show, currentUrl } = this.state;
    const {
      current,
      translate,
      withOverlay,
      readOnly,
      isUrl,
      isPlaying,
      playingSrc,
      wrapperClassname = '',
      title: propsTitle,
      disabledButton,
      showEmptyControls = false,
    } = this.props;

    const isCurrentPlaying = currentUrl === playingSrc && isPlaying;
    const title =
      propsTitle !== undefined ? propsTitle :
      readOnly ? translate(1330, 'Play Audio') :
      translate(13, 'Add Audio');

    return (
      <>
        <div className={wrapperClassname}>
          <span className={classes.label}>{title}</span>
          <div className={classes['buttons-wrapper']}>
            {!readOnly && (
              <button
                disabled={disabledButton}
                type='button'
                title={title}
                className={`${classes['btn']} add_audio btn-effect`}
                onClick={this.toggleShow}
              >
                <img
                  src={`${imagesURL}/FrontendUtilities/addaudio.png`}
                  alt={title}
                />
              </button>
            )}

            {(current || isUrl || showEmptyControls) && (
              <>
                {isCurrentPlaying ? (
                  <button
                    type='button'
                    title={translate(73, 'Stop Audio')}
                    className={`${classes['btn']} stop_audio btn-effect`}
                    onClick={this.stopHandler}
                  >
                    <img
                      src={`${imagesURL}/FrontendUtilities/stop1.png`}
                      alt={translate(73, 'Stop Audio')}
                    />
                  </button>
                ) : (
                  <button
                    type='button'
                    title={translate(514, 'Play')}
                    disabled={!current && !isUrl}
                    className={`${classes['btn']} audio_make btn-effect`}
                    onClick={this.playHandler}
                  >
                    <img
                      src={`${imagesURL}/Images/Audio/audio_make.png`}
                      alt={translate(514, 'Play')}
                    />
                  </button>
                )}
                {!readOnly && (
                  <button
                    type='button'
                    title={translate(96, 'Delete')}
                    disabled={!current && !isUrl}
                    className={`${classes['btn']} delete_audio btn-effect`}
                    onClick={this.removeHandler}
                  >
                    <img
                      src={`${imagesURL}/FrontendUtilities/delete1.png`}
                      alt={translate(96, 'Delete')}
                    />
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {show && (
          <DraggableModal
            title={translate(13, 'Add Audio')}
            show={show}
            withOverlay={withOverlay}
            onClose={this.toggleShow}
          >
            <div className={classes['add-audio-modal']}>
              <div className={classes['top-row']}>
                <div className={classes['upload-col']}>
                  <Dropzone
                    onDrop={this.onDrop}
                    accept=".mp3"
                    multiple={false}
                    maxSize={6 * 1000000}
                  >
                    {({ getRootProps, getInputProps, isDragActive }) => (
                      <div
                        {...getRootProps()}
                        className={classNames(classes['dropzone'], {
                          [`${classes['dropzone--active']}`]: isDragActive,
                        })}
                      >
                        <input {...getInputProps()} />
                        <p>
                          {translate(
                            441,
                            `Drop .mp3 file here, or click to select file to upload. Max 6mb`
                          )}
                        </p>
                      </div>
                    )}
                  </Dropzone>
                </div>
                <div className={classes['record-col']}>
                  <p className={classes['record-instructions']}>
                    {translate(436, 'Click the Start button to record. When you are done, click Play button to hear your recording or Use Recorded File button to add it to the activity.')}
                  </p>
                  <div className={classes['record-wrapper']}>
                    <Record
                      translate={translate}
                      addRecordedFileHandler={this.useRecorderFile}
                    />
                  </div>
                </div>
              </div>

              <div className={classes['tts-row']}>
                {this.renderPollyControls()}
                {this.renderElevenLabsControls()}
                <HaitianTtsControls
                  locale={this.getEffectiveHaitianLocale()}
                  associatedText={this.props.associatedText || ''}
                  disabledButton={this.props.disabledButton}
                  defaultVoice={this.props.tts?.voice || 'female'}
                  onGenerate={this.onGenerateHaitianTTS}
                />
              </div>
            </div>
          </DraggableModal>
        )}
      </>
    );
  }
}

AddAudio.propTypes = {
  wrapperClassname: PropTypes.string,
  removeOldOnChange: PropTypes.bool,
  filename: PropTypes.string,
  withId: PropTypes.bool,
  saveAfterChange: PropTypes.bool,
  withOverlay: PropTypes.bool,
  isUrl: PropTypes.bool,
  useFileBucket: PropTypes.bool,
  readOnly: PropTypes.bool,
  path: PropTypes.string.isRequired,
  current: PropTypes.string,
  onChangeHandler: PropTypes.func.isRequired,
  translate: PropTypes.func.isRequired,
  uploadAudioFile: PropTypes.func.isRequired,
  generateTTS: PropTypes.func.isRequired,
  setTTSVoice: PropTypes.func.isRequired,
  setTTSLocale: PropTypes.func.isRequired,
  setElevenLabsTTSLocale: PropTypes.func.isRequired,
  setElevenLabsTTSAccent: PropTypes.func.isRequired,
  setElevenLabsTTSVoice: PropTypes.func.isRequired,
  saveEditingActivity: PropTypes.func.isRequired,
  playingSrc: PropTypes.string,
  isPlaying: PropTypes.bool,
  versionType: PropTypes.string,
  disabledButton: PropTypes.bool,
  isWordAgs: PropTypes.bool,
  autoOpenKey: PropTypes.any,
  onGenerateElevenLabsOverride: PropTypes.func,
  onGeneratePollyOverride: PropTypes.func,
  onGenerateHaitianOverride: PropTypes.func,
  onModalClose: PropTypes.func,
  allowedLocales: PropTypes.arrayOf(PropTypes.string),
  allLocales: PropTypes.array,
};

const filterLocalesForAddAudio = (locales = [], allowedLocales) => {
  if (!Array.isArray(allowedLocales)) return locales;

  const allowed = new Set(allowedLocales.filter(Boolean));
  return locales.filter((locale) => allowed.has(locale.code));
};

const mapStateToProps = ({
  status: { translate },
  audio: { audioSrc, isPlaying },
  flinkMake: { tts },
  common: { locales },
}, ownProps) => {
  const availableLocales = filterLocalesForAddAudio(locales, ownProps.allowedLocales);

  return {
    translate,
    playingSrc: audioSrc,
    isPlaying,
    tts,
    locales: availableLocales.filter(isPollyEnabledForLocale),
    elevenLabsLocales: availableLocales.filter(isElevenLabsEnabledForLocale),
    allLocales: availableLocales,
  };
};

export default compose(
  connect(mapStateToProps, {
    uploadAudioFile,
    generateTTS,
    setTTSVoice,
    setTTSLocale,
    setElevenLabsTTSLocale,
    setElevenLabsTTSAccent,
    setElevenLabsTTSVoice,
    saveEditingActivity,
  })
)(AddAudio);
